import { useEffect, useState } from "react";
import { Table, Button, Tag, DatePicker, Select, Input, Row, Col, Card, Collapse, Modal, Tooltip, Tabs } from "antd";
import { Mail, Eye, Search, Filter, FileText, HelpCircle, AlertCircle, Send, Clock } from "lucide-react";
import dayjs from "dayjs";

import usePassengers from "../hooks/usePassengers";
import EmailParserModal from "../components/EmailParserModal";
import AutomaticEmailConfig from "../components/AutomaticEmailConfig";
import PageDocumentation from "../components/PageDocumentation";

const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  { value: "", label: "Todos los estados" },
  { value: "PENDING", label: "Pendiente" },
  { value: "PROCESSING", label: "Procesando" },
  { value: "PROCESSED", label: "Procesado" },
  { value: "FAILED", label: "Fallido" },
];

const STATUS_COLORS = {
  PENDING: "orange",
  PROCESSING: "blue",
  PROCESSED: "green",
  FAILED: "red",
};

const SENT_STATUS_COLORS = {
  SENT: "green",
  FAILED: "red",
};

function EmailImportHistory() {
  const {
    getEmailImports,
    getEmailImportDetail,
    emailImports,
    emailImportDetail,
    setEmailImportDetail,
    getSentEmails,
    sentEmails,
    loading,
  } = usePassengers();

  const [activeTab, setActiveTab] = useState("received");
  const [params, setParams] = useState({ page: 1 });
  const [sentParams, setSentParams] = useState({ page: 1 });
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [sentDateRange, setSentDateRange] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [sentSearch, setSentSearch] = useState("");

  // State for email parser modal (PENDING emails)
  const [parserModalOpen, setParserModalOpen] = useState(false);
  const [selectedPendingEmail, setSelectedPendingEmail] = useState(null);
  const [faqModalOpen, setFaqModalOpen] = useState(false);
  const [unidentifiedEmailModal, setUnidentifiedEmailModal] = useState({ open: false, email: null });
  const [sentEmailDetailModal, setSentEmailDetailModal] = useState({ open: false, email: null });

  // Build params for API call (received emails)
  const buildParams = () => {
    const newParams = { page: params.page };
    if (dateRange && dateRange[0] && dateRange[1]) {
      newParams.date_from = dateRange[0].format("YYYY-MM-DD");
      newParams.date_to = dateRange[1].format("YYYY-MM-DD");
    }
    if (status) {
      newParams.status = status;
    }
    if (search) {
      newParams.search = search;
    }
    return newParams;
  };

  // Build params for sent emails
  const buildSentParams = () => {
    const newParams = { page: sentParams.page };
    if (sentDateRange && sentDateRange[0] && sentDateRange[1]) {
      newParams.date_from = sentDateRange[0].format("YYYY-MM-DD");
      newParams.date_to = sentDateRange[1].format("YYYY-MM-DD");
    }
    if (sentSearch) {
      newParams.search = sentSearch;
    }
    return newParams;
  };

  // Fetch on params change (received emails)
  useEffect(() => {
    if (activeTab === "received") {
      getEmailImports(buildParams());
    }
  }, [params.page, dateRange, status, search, activeTab]);

  // Fetch on params change (sent emails)
  useEffect(() => {
    if (activeTab === "sent") {
      getSentEmails(buildSentParams());
    }
  }, [sentParams.page, sentDateRange, sentSearch, activeTab]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === "received") {
        getEmailImports(buildParams());
      } else {
        getSentEmails(buildSentParams());
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [params.page, dateRange, status, search, sentParams.page, sentDateRange, sentSearch, activeTab]);

  const handleViewDetail = async (importId) => {
    await getEmailImportDetail(importId);
    setDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailModalOpen(false);
    setEmailImportDetail(null);
  };

  const handleTableChange = (pagination) => {
    setParams({ ...params, page: pagination.current });
  };

  const handleSentTableChange = (pagination) => {
    setSentParams({ ...sentParams, page: pagination.current });
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    setParams({ ...params, page: 1 });
  };

  const handleSentDateChange = (dates) => {
    setSentDateRange(dates);
    setSentParams({ ...sentParams, page: 1 });
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    setParams({ ...params, page: 1 });
  };

  const handleSearch = (value) => {
    setSearch(value);
    setParams({ ...params, page: 1 });
  };

  const handleSentSearch = (value) => {
    setSentSearch(value);
    setSentParams({ ...sentParams, page: 1 });
  };

  // Handle opening parser modal for PENDING emails
  const handleProcessPending = async (record) => {
    await getEmailImportDetail(record.id);
    setSelectedPendingEmail(record);
    setParserModalOpen(true);
  };

  const handleCloseParser = () => {
    setParserModalOpen(false);
    setSelectedPendingEmail(null);
  };

  const handleParserSuccess = () => {
    // Refresh the list after successful import
    getEmailImports(params);
  };

  // Columns for received emails
  const receivedColumns = [
    {
      title: "Fecha",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date) => dayjs(date).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Asunto",
      dataIndex: "subject",
      key: "subject",
      ellipsis: true,
      render: (subject) => subject || <span className="text-gray-400">Sin asunto</span>,
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (st) => (
        <Tag color={STATUS_COLORS[st] || "default"}>
          {STATUS_OPTIONS.find(o => o.value === st)?.label || st}
        </Tag>
      ),
    },
    {
      title: "Email Remitente",
      dataIndex: "senderEmail",
      key: "senderEmail",
      width: 200,
      ellipsis: true,
      render: (email, record) => {
        if (!email) return <span className="text-gray-400">-</span>;

        if (!record.isSellerIdentified) {
          return (
            <Tooltip title="Email no identificado - Click para más info">
              <span
                className="text-orange-500 cursor-pointer hover:underline flex items-center gap-1"
                onClick={() => setUnidentifiedEmailModal({ open: true, email })}
              >
                <AlertCircle size={14} />
                {email}
              </span>
            </Tooltip>
          );
        }

        return <span className="text-green-600">{email}</span>;
      },
    },
    {
      title: "Importado por",
      dataIndex: "importedByName",
      key: "importedByName",
      width: 150,
      render: (name) => name || <span className="text-gray-400">Desconocido</span>,
    },
    {
      title: "Pasajeros",
      dataIndex: "passengersCount",
      key: "passengersCount",
      width: 90,
      align: "center",
      render: (count) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: "",
      key: "actions",
      width: 150,
      render: (_, record) =>
        record.status === "PENDING" ? (
          <Button
            type="primary"
            size="small"
            icon={<FileText size={14} />}
            onClick={() => handleProcessPending(record)}
          >
            Procesar Email
          </Button>
        ) : (
          <Button
            size="small"
            icon={<Eye size={14} />}
            onClick={() => handleViewDetail(record.id)}
          >
            Ver
          </Button>
        ),
    },
  ];

  // Columns for sent emails
  const sentColumns = [
    {
      title: "Fecha",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date) => dayjs(date).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Asunto",
      dataIndex: "subject",
      key: "subject",
      ellipsis: true,
    },
    {
      title: "Destinatario",
      dataIndex: "recipientEmail",
      key: "recipientEmail",
      width: 200,
      ellipsis: true,
      render: (email, record) => (
        <span>
          {record.recipientUserName || record.recipientName || email}
          <br />
          <span className="text-gray-400 text-xs">{email}</span>
        </span>
      ),
    },
    {
      title: "Tipo",
      dataIndex: "emailTypeDisplay",
      key: "emailTypeDisplay",
      width: 150,
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (st, record) => (
        <Tag color={SENT_STATUS_COLORS[st] || "default"}>
          {record.statusDisplay}
        </Tag>
      ),
    },
    {
      title: "Adjunto",
      dataIndex: "hasAttachment",
      key: "hasAttachment",
      width: 100,
      align: "center",
      render: (has, record) =>
        has ? (
          <Tooltip title={record.attachmentFilename}>
            <Tag color="purple">Sí</Tag>
          </Tooltip>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Button
          size="small"
          icon={<Eye size={14} />}
          onClick={() => setSentEmailDetailModal({ open: true, email: record })}
        >
          Ver
        </Button>
      ),
    },
  ];

  const tabItems = [
    {
      key: "received",
      label: (
        <span className="flex items-center gap-2">
          <Mail size={16} />
          Recibidos
        </span>
      ),
      children: (
        <>
          {/* Filtros Recibidos */}
          <Card className="mb-4" size="small">
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={8}>
                <div className="flex items-center gap-2">
                  <Filter size={16} className="text-gray-400" />
                  <RangePicker
                    value={dateRange}
                    onChange={handleDateChange}
                    format="DD/MM/YYYY"
                    placeholder={["Desde", "Hasta"]}
                    style={{ width: "100%" }}
                    allowClear
                  />
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  value={status}
                  onChange={handleStatusChange}
                  options={STATUS_OPTIONS}
                  style={{ width: "100%" }}
                  placeholder="Estado"
                />
              </Col>
              <Col xs={24} sm={12} md={10}>
                <Input.Search
                  placeholder="Buscar por asunto o contenido..."
                  allowClear
                  onSearch={handleSearch}
                  enterButton={<Search size={16} />}
                />
              </Col>
            </Row>
          </Card>

          <Table
            columns={receivedColumns}
            dataSource={emailImports.results}
            rowKey="id"
            loading={loading.get && activeTab === "received"}
            pagination={{
              current: params.page,
              total: emailImports.count,
              pageSize: 40,
              showSizeChanger: false,
            }}
            onChange={handleTableChange}
          />
        </>
      ),
    },
    {
      key: "sent",
      label: (
        <span className="flex items-center gap-2">
          <Send size={16} />
          Enviados Automáticamente
        </span>
      ),
      children: (
        <>
          {/* Filtros Enviados */}
          <Card className="mb-4" size="small">
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={10}>
                <div className="flex items-center gap-2">
                  <Filter size={16} className="text-gray-400" />
                  <RangePicker
                    value={sentDateRange}
                    onChange={handleSentDateChange}
                    format="DD/MM/YYYY"
                    placeholder={["Desde", "Hasta"]}
                    style={{ width: "100%" }}
                    allowClear
                  />
                </div>
              </Col>
              <Col xs={24} sm={12} md={14}>
                <Input.Search
                  placeholder="Buscar por asunto o destinatario..."
                  allowClear
                  onSearch={handleSentSearch}
                  enterButton={<Search size={16} />}
                />
              </Col>
            </Row>
          </Card>

          <Table
            columns={sentColumns}
            dataSource={sentEmails.results}
            rowKey="id"
            loading={loading.get && activeTab === "sent"}
            pagination={{
              current: sentParams.page,
              total: sentEmails.count,
              pageSize: 40,
              showSizeChanger: false,
            }}
            onChange={handleSentTableChange}
          />
        </>
      ),
    },
    {
      key: "scheduled",
      label: (
        <span className="flex items-center gap-2">
          <Clock size={16} />
          Programados
        </span>
      ),
      children: <AutomaticEmailConfig />,
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Mail size={24} />
          <h1 className="text-xl font-bold">Mails Vendedores</h1>
        </div>
        <div className="flex items-center gap-2">
          <PageDocumentation docKey="email-import-history" />
          <Button
            type="text"
            icon={<HelpCircle size={20} />}
            onClick={() => setFaqModalOpen(true)}
            title="Ayuda y preguntas frecuentes"
          />
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />

      {/* Detail Modal using EmailParserModal with readOnly */}
      <EmailParserModal
        externalOpen={detailModalOpen}
        onClose={handleCloseDetail}
        initialContent={emailImportDetail?.content || ""}
        initialSubject={emailImportDetail?.subject || ""}
        hideButton={true}
        readOnly={true}
        initialPassengers={emailImportDetail?.passengers || null}
      />

      {/* Email Parser Modal for PENDING emails */}
      <EmailParserModal
        externalOpen={parserModalOpen}
        onClose={handleCloseParser}
        initialContent={emailImportDetail?.content || ""}
        initialSubject={emailImportDetail?.subject || ""}
        emailImportId={selectedPendingEmail?.id}
        hideButton={true}
        onSuccess={handleParserSuccess}
        initialParsedData={emailImportDetail?.parsedData || null}
      />

      {/* Sent Email Detail Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <Send size={20} />
            <span>Detalle de Email Enviado</span>
          </div>
        }
        open={sentEmailDetailModal.open}
        onCancel={() => setSentEmailDetailModal({ open: false, email: null })}
        footer={<Button onClick={() => setSentEmailDetailModal({ open: false, email: null })}>Cerrar</Button>}
        width={700}
      >
        {sentEmailDetailModal.email && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-gray-500">Fecha:</span>
                <p className="font-medium">
                  {dayjs(sentEmailDetailModal.email.createdAt).format("DD/MM/YYYY HH:mm")}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Estado:</span>
                <p>
                  <Tag color={SENT_STATUS_COLORS[sentEmailDetailModal.email.status]}>
                    {sentEmailDetailModal.email.statusDisplay}
                  </Tag>
                </p>
              </div>
              <div>
                <span className="text-gray-500">Destinatario:</span>
                <p className="font-medium">
                  {sentEmailDetailModal.email.recipientUserName || sentEmailDetailModal.email.recipientName}
                </p>
                <p className="text-gray-400 text-sm">{sentEmailDetailModal.email.recipientEmail}</p>
              </div>
              <div>
                <span className="text-gray-500">Tipo:</span>
                <p>
                  <Tag color="blue">{sentEmailDetailModal.email.emailTypeDisplay}</Tag>
                </p>
              </div>
            </div>

            <div>
              <span className="text-gray-500">Asunto:</span>
              <p className="font-medium">{sentEmailDetailModal.email.subject}</p>
            </div>

            {sentEmailDetailModal.email.hasAttachment && (
              <div>
                <span className="text-gray-500">Archivo adjunto:</span>
                <p>
                  <Tag color="purple">{sentEmailDetailModal.email.attachmentFilename}</Tag>
                </p>
              </div>
            )}

            <div>
              <span className="text-gray-500">Contenido:</span>
              <div className="bg-gray-50 p-4 rounded mt-1 whitespace-pre-wrap text-sm">
                {sentEmailDetailModal.email.body}
              </div>
            </div>

            {sentEmailDetailModal.email.errorMessage && (
              <div>
                <span className="text-red-500">Error:</span>
                <p className="text-red-600">{sentEmailDetailModal.email.errorMessage}</p>
              </div>
            )}

            {sentEmailDetailModal.email.contextData && (
              <div>
                <span className="text-gray-500">Datos adicionales:</span>
                <pre className="bg-gray-100 p-2 rounded text-xs mt-1 overflow-auto">
                  {JSON.stringify(sentEmailDetailModal.email.contextData, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Unidentified Email Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-orange-500">
            <AlertCircle size={20} />
            <span>Email No Identificado</span>
          </div>
        }
        open={unidentifiedEmailModal.open}
        onCancel={() => setUnidentifiedEmailModal({ open: false, email: null })}
        footer={
          <div className="flex gap-2 justify-end">
            <Button onClick={() => setUnidentifiedEmailModal({ open: false, email: null })}>
              Cerrar
            </Button>
            <Button
              type="primary"
              href={`${process.env.REACT_APP_API_URL}admin/users/customuser/`}
              target="_blank"
            >
              Ir al Panel de Administración
            </Button>
          </div>
        }
        width={500}
      >
        <div className="py-4">
          <p className="mb-3">
            El email <strong className="text-orange-500">{unidentifiedEmailModal.email}</strong> no está
            registrado como vendedor en el sistema.
          </p>
          <p className="mb-3">
            Para que los emails de este remitente se procesen automáticamente, debe:
          </p>
          <ol className="list-decimal pl-5 space-y-2 mb-4">
            <li>
              Ingresar al{" "}
              <a
                href={`${process.env.REACT_APP_API_URL}admin/users/customuser/`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                panel de administración de usuarios
              </a>
            </li>
            <li>Buscar al vendedor correspondiente</li>
            <li>
              Asignarle el email <strong>{unidentifiedEmailModal.email}</strong> en el campo de email
            </li>
            <li>Guardar los cambios</li>
          </ol>
          <p className="text-gray-500 text-sm">
            Mientras tanto, puede procesar los emails manualmente haciendo clic en "Procesar Email".
          </p>
        </div>
      </Modal>

      {/* FAQ Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <HelpCircle size={20} />
            <span>Preguntas Frecuentes</span>
          </div>
        }
        open={faqModalOpen}
        onCancel={() => setFaqModalOpen(false)}
        footer={<Button onClick={() => setFaqModalOpen(false)}>Cerrar</Button>}
        width={700}
      >
        <Collapse
          accordion
          defaultActiveKey={["1"]}
          items={[
            {
              key: "1",
              label: "¿Cual es el formato esperado de los emails de vendedores?",
              children: (
                <div className="text-sm">
                  <p className="mb-2">El sistema utiliza IA para interpretar los emails, pero funciona mejor con este formato:</p>
                  <pre className="bg-gray-100 p-3 rounded text-xs mb-3 whitespace-pre-wrap">
{`Hotel Costa Mar

Viernes Sierra:
Garcia x2 15000
Rodriguez x3+1 25000
Lopez x1+1L 8000

Sabado Miramar:
Perez x2 20000
Martinez x1 10000`}
                  </pre>
                  <p className="mb-2"><strong>Interpretacion de cantidades:</strong></p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><code className="bg-gray-100 px-1">x2</code> = 2 adultos</li>
                    <li><code className="bg-gray-100 px-1">x3+1</code> = 3 adultos + 1 menor</li>
                    <li><code className="bg-gray-100 px-1">x2+1L</code> = 2 adultos + 1 liberado (gratis)</li>
                    <li><code className="bg-gray-100 px-1">x1+1 L</code> = 1 adulto + 1 liberado</li>
                  </ul>
                  <p className="mt-2 text-gray-500">El numero al final de cada linea es el monto pagado.</p>
                </div>
              ),
            },
            {
              key: "2",
              label: "¿Que significan los estados de los emails?",
              children: (
                <div className="text-sm space-y-2">
                  <p><Tag color="orange">Pendiente</Tag> El email fue recibido pero aun no se procesaron los pasajeros.</p>
                  <p><Tag color="blue">Procesando</Tag> El email esta siendo analizado por la IA.</p>
                  <p><Tag color="green">Procesado</Tag> Los pasajeros fueron cargados exitosamente al sistema.</p>
                  <p><Tag color="red">Fallido</Tag> Hubo un error al procesar el email. Puede intentar procesarlo manualmente.</p>
                </div>
              ),
            },
            {
              key: "3",
              label: "¿Como proceso un email pendiente?",
              children: (
                <div className="text-sm">
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>Busque el email en la lista con estado <Tag color="orange">Pendiente</Tag></li>
                    <li>Haga clic en el boton <strong>"Procesar Email"</strong></li>
                    <li>Revise los pasajeros parseados y corrija si es necesario</li>
                    <li>Seleccione el <strong>Grupo</strong> y <strong>Vendedor</strong> correspondiente</li>
                    <li>Verifique que cada pasajero tenga hotel y excursion asignados</li>
                    <li>Haga clic en <strong>"Guardar Pasajeros"</strong></li>
                  </ol>
                </div>
              ),
            },
            {
              key: "4",
              label: "¿Que hago si la IA no reconocio bien un pasajero?",
              children: (
                <div className="text-sm">
                  <p className="mb-2">En la pantalla de revision puede:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Editar el nombre del pasajero directamente</li>
                    <li>Cambiar la cantidad de adultos, menores o liberados</li>
                    <li>Modificar el monto pagado</li>
                    <li>Cambiar el hotel o excursion usando los selectores</li>
                    <li>Eliminar pasajeros incorrectos con el boton X</li>
                    <li>Agregar pasajeros manualmente con "Agregar Pasajero"</li>
                  </ul>
                </div>
              ),
            },
            {
              key: "5",
              label: "¿Como cambio el hotel o excursion de varios pasajeros a la vez?",
              children: (
                <div className="text-sm">
                  <p>En la vista de revision, los pasajeros estan agrupados por hotel y excursion. Puede cambiar el hotel o excursion de todo un grupo usando el selector que aparece en el encabezado de cada seccion.</p>
                  <p className="mt-2">Use el boton <strong>"Cambiar agrupacion"</strong> para alternar entre:</p>
                  <ul className="list-disc pl-5 mt-1">
                    <li><strong>Hotel → Excursion:</strong> Agrupa primero por hotel</li>
                    <li><strong>Excursion → Hotel:</strong> Agrupa primero por excursion</li>
                  </ul>
                </div>
              ),
            },
            {
              key: "6",
              label: "¿Por que algunos emails no se procesan automaticamente?",
              children: (
                <div className="text-sm">
                  <p className="mb-2">El sistema solo procesa automaticamente los emails cuyo remitente esta registrado como <strong>vendedor</strong> en el sistema.</p>
                  <p className="mb-2">Para que un email se procese automaticamente:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>El email del remitente debe coincidir con el email de un usuario vendedor</li>
                    <li>O el nombre del remitente debe coincidir con el nombre de un vendedor</li>
                  </ul>
                  <p className="mt-3 p-2 bg-blue-50 rounded">
                    <strong>Tip:</strong> Si un vendedor envia emails desde una direccion no registrada, puede agregar ese email a su perfil de usuario en el{" "}
                    <a href={`${process.env.REACT_APP_API_URL}admin/users/customuser/`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                      panel de administracion
                    </a>, o procesar el email manualmente.
                  </p>
                </div>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
}

export default EmailImportHistory;
